import { useTranslation } from 'react-i18next';
import { WeekRangeFilter } from './ui/week-filter';
import React, { useMemo, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, AlertTriangle, Calendar, Filter } from 'lucide-react';
import { useDisplayCurrency, SYMBOL } from '../hooks/useDisplayCurrency';
import DisplayCurrencySwitcher from './DisplayCurrencySwitcher';
import { CopyNumberButton } from './shared/CopyNumberButton';
import { ResetFiltersButton } from './shared/ResetFiltersButton';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7c43'];
const CATEGORY_COLORS = {
  travel: '#0088FE',
  accommodation: '#00C49F',
  tools: '#FFBB28',
  consumables: '#a855f7',
  meals: '#ec4899',
  other: '#82ca9d',
  labor: '#ff7c43',
  equipment: '#14b8a6'
};

const PieLegend = ({ data, format }) => {
  const total = data.reduce((sum, d) => sum + d.value, 0) || 1;
  return (
    <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs" data-testid="cost-pie-legend">
      {data.map((d) => {
        const pct = (d.value / total) * 100;
        return (
          <li key={d.name} className="flex items-center gap-2 min-w-0">
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: d.color }} />
            <span className="truncate text-gray-700">{d.name}</span>
            <span className="ml-auto whitespace-nowrap tabular-nums text-gray-900">
              {format(d.value)} ({pct > 0 && pct < 1 ? '<1' : pct.toFixed(0)}%)
            </span>
          </li>
        );
      })}
    </ul>
  );
};

const BudgetDashboard = ({ budgetSummaries, projects, expenses, dateRange, onDateRangeChange, onResetFilters }) => {
  const { t, i18n } = useTranslation();
  // Delay chart rendering to avoid width/height -1 warnings from ResponsiveContainer
  const [chartsReady, setChartsReady] = useState(false);
  const containerRef = React.useRef(null);
  
  useEffect(() => {
    let observer = null;
    let mounted = true;
    
    // Use requestAnimationFrame to wait for layout
    const checkAndSetReady = () => {
      if (!mounted) return;
      
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setChartsReady(true);
          return;
        }
      }
      
      // If still not ready, use ResizeObserver
      if (containerRef.current && !observer) {
        observer = new ResizeObserver((entries) => {
          for (const entry of entries) {
            if (entry.contentRect.width > 0 && mounted) {
              setChartsReady(true);
              observer.disconnect();
              observer = null;
            }
          }
        });
        observer.observe(containerRef.current);
      }
    };
    
    // Wait for next frame then check
    requestAnimationFrame(() => {
      requestAnimationFrame(checkAndSetReady);
    });
    
    return () => {
      mounted = false;
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  // Display currency switcher — values come from the backend already
  // converted (PLN canonical → display_currency via NBP today's rate).
  // The `*_display` fields are what we render; fall back to `*_pln`
  // when the backend is older and didn't include the new fields.
  const { currency: displayCurrency } = useDisplayCurrency();
  const pickDisp = (s, key) => (s && (s[`${key}_display`] != null ? s[`${key}_display`] : s[`${key}_pln`])) || 0;

  // Format currency
  const formatCurrency = (value) => {
    const cur = displayCurrency || 'PLN';
    const formatted = new Intl.NumberFormat('pl-PL', {
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(value || 0);
    if (cur === 'PLN') return `${formatted} zł`;
    return `${SYMBOL[cur] || ''}${formatted}`;
  };
  
  // Year options for quick selection
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];

  // Check if "Show All" is active (no date range set)
  const isShowingAll = !dateRange?.startDate && !dateRange?.endDate;

  // Filter to only include projects with activity in the selected period
  const activeProjectSummaries = useMemo(() => {
    if (isShowingAll) {
      return Object.values(budgetSummaries);
    }
    return Object.values(budgetSummaries).filter(s => 
      s && (s.revenue_pln > 0 || s.total_costs_pln > 0 || s.total_hours > 0)
    );
  }, [budgetSummaries, isShowingAll]);

  // Calculate totals - only from active projects in the period
  const totals = useMemo(() => {
    return {
      revenue: activeProjectSummaries.reduce((sum, s) => sum + pickDisp(s, 'revenue'), 0),
      costs: activeProjectSummaries.reduce((sum, s) => sum + pickDisp(s, 'total_costs'), 0),
      labor: activeProjectSummaries.reduce((sum, s) => sum + pickDisp(s, 'labor_cost'), 0),
      expenses: activeProjectSummaries.reduce((sum, s) => sum + pickDisp(s, 'expense_cost'), 0),
      profit: activeProjectSummaries.reduce((sum, s) => sum + pickDisp(s, 'profit'), 0),
      hours: activeProjectSummaries.reduce((sum, s) => sum + (s?.total_hours || 0), 0)
    };
  }, [activeProjectSummaries, displayCurrency]); // eslint-disable-line

  // Prepare data for Budget vs Actual chart - only projects with data
  const budgetVsActualData = useMemo(() => {
    return projects
      .filter(p => budgetSummaries[p.id])
      .filter(p => {
        // When filtering by date, only show projects with activity in that period
        if (isShowingAll) return true;
        const summary = budgetSummaries[p.id];
        return summary && (summary.revenue_pln > 0 || summary.total_costs_pln > 0 || summary.total_hours > 0);
      })
      .map(p => {
        const summary = budgetSummaries[p.id];
        return {
          name: p.name.length > 15 ? p.name.substring(0, 15) + '...' : p.name,
          fullName: p.name,
          revenue: pickDisp(summary, 'revenue'),
          costs: pickDisp(summary, 'total_costs'),
          profit: pickDisp(summary, 'profit'),
          budgetType: summary?.budget_type
        };
      })
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 8); // Top 8 projects
  }, [projects, budgetSummaries, isShowingAll]);

  // Prepare data for Expense by Category pie chart - only from active projects
  // Percent inside each slice; small slices (<6%) are left to the legend so labels never overlap.
  const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.06) return null;
    const r = innerRadius + (outerRadius - innerRadius) * 0.6;
    const x = cx + r * Math.cos((-midAngle * Math.PI) / 180);
    const y = cy + r * Math.sin((-midAngle * Math.PI) / 180);
    return (
      <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={600}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };
  const expenseByCategoryData = useMemo(() => {
    const categoryTotals = {};
    
    activeProjectSummaries.forEach(summary => {
      const cats = summary?.expense_by_category_display || summary?.expense_by_category;
      if (cats) {
        Object.entries(cats).forEach(([cat, amount]) => {
          categoryTotals[cat] = (categoryTotals[cat] || 0) + amount;
        });
      }
    });

    // Add labor costs as a category (same display currency as the expense categories)
    const laborTotal = activeProjectSummaries.reduce((sum, s) => sum + pickDisp(s, 'labor_cost'), 0);
    if (laborTotal > 0) {
      categoryTotals['labor'] = laborTotal;
    }

    return Object.entries(categoryTotals)
      .map(([name, value]) => ({
        name: t(`budget.costCategory.${name}`, { defaultValue: name.charAt(0).toUpperCase() + name.slice(1) }),
        value: Math.round(value),
        color: CATEGORY_COLORS[name] || '#8884d8'
      }))
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [activeProjectSummaries, displayCurrency, t]); // eslint-disable-line

  // Prepare data for Profit Margin chart - only projects with revenue in the period
  const profitMarginData = useMemo(() => {
    return projects
      .filter(p => budgetSummaries[p.id] && budgetSummaries[p.id].revenue_pln > 0)
      .map(p => {
        const summary = budgetSummaries[p.id];
        return {
          name: p.name.length > 12 ? p.name.substring(0, 12) + '...' : p.name,
          fullName: p.name,
          margin: summary?.profit_margin_percent || 0,
          isHealthy: (summary?.profit_margin_percent || 0) >= 20
        };
      })
      .sort((a, b) => b.margin - a.margin);
  }, [projects, budgetSummaries]);

  // Prepare expense trend data (by month from expenses)
  const expenseTrendData = useMemo(() => {
    if (!expenses || expenses.length === 0) return [];

    const monthlyData = {};
    
    expenses.forEach(exp => {
      if (exp.status === 'admin_approved' || exp.status === 'paid') {
        const date = new Date(exp.expense_date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = { travel: 0, accommodation: 0, tools: 0, consumables: 0, meals: 0, other: 0 };
        }
        
        const category = exp.category || 'other';
        monthlyData[monthKey][category] = (monthlyData[monthKey][category] || 0) + (exp.amount_pln || 0);
      }
    });

    return Object.entries(monthlyData)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6) // Last 6 months
      .map(([month, data]) => ({
        month: new Date(month + '-01').toLocaleDateString(i18n.language?.startsWith('pl') ? 'pl-PL' : 'en-GB', { month: 'short', year: '2-digit' }),
        ...data,
        total: Object.values(data).reduce((sum, val) => sum + val, 0)
      }));
  }, [expenses]);

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded shadow-lg">
          <p className="font-semibold text-sm">{payload[0]?.payload?.fullName || label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Calculate health indicators - only from active projects in the period
  const healthIndicators = useMemo(() => {
    const profitableProjects = activeProjectSummaries.filter(s => (s?.profit_pln || 0) > 0).length;
    const atRiskProjects = activeProjectSummaries.filter(s => (s?.profit_margin_percent || 0) < 10 && (s?.profit_margin_percent || 0) >= 0 && (s?.revenue_pln > 0 || s?.total_costs_pln > 0)).length;
    const lossProjects = activeProjectSummaries.filter(s => (s?.profit_pln || 0) < 0).length;
    
    return {
      profitableProjects,
      atRiskProjects,
      lossProjects,
      avgMargin: activeProjectSummaries.length > 0 
        ? activeProjectSummaries.reduce((sum, s) => sum + (s?.profit_margin_percent || 0), 0) / activeProjectSummaries.length 
        : 0
    };
  }, [activeProjectSummaries]);

  const noData = activeProjectSummaries.length === 0;

  // Handle year quick select
  const handleYearSelect = (year) => {
    if (onDateRangeChange) {
      onDateRangeChange({
        startDate: `${year}-01-01`,
        endDate: `${year}-12-31`
      });
    }
  };
  
  // Handle show all (no date filter)
  const handleShowAll = () => {
    if (onDateRangeChange) {
      onDateRangeChange({
        startDate: '',
        endDate: ''
      });
    }
  };
  
  // Get selected year from date range
  const selectedYear = dateRange?.startDate ? parseInt(dateRange.startDate.substring(0, 4)) : null;

  return (
    <div ref={containerRef} className="space-y-6">
      {/* Date Filter Row */}
      <Card className="bg-gray-50">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">{t('budget.period')}</span>
            </div>
            
            {/* Show All button */}
            <Button
              variant={isShowingAll ? "default" : "outline"}
              size="sm"
              onClick={handleShowAll}
              className="px-3"
             data-testid="btn-all-time">
              {t('budget.allTime')}
            </Button>
            
            <div className="h-6 w-px bg-gray-300" />
            
            {/* Year quick select buttons */}
            <div className="flex gap-1">
              {yearOptions.map(year => (
                <Button
                  key={year}
                  variant={selectedYear === year && !isShowingAll ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleYearSelect(year)}
                  className="px-3"
                >
                  {year}
                </Button>
              ))}
            </div>
            
            <div className="h-6 w-px bg-gray-300" />
            
            {/* Custom date range */}
            <div className="flex items-center gap-2">
              <Label className="text-sm text-gray-500">{t('budget.from')}</Label>
              <Input
                type="date"
                value={dateRange?.startDate || ''}
                onChange={(e) => onDateRangeChange?.({ ...dateRange, startDate: e.target.value })}
                className="h-8 w-44"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-gray-500">{t('budget.to')}</Label>
              <Input
                type="date"
                value={dateRange?.endDate || ''}
                onChange={(e) => onDateRangeChange?.({ ...dateRange, endDate: e.target.value })}
                className="h-8 w-44"
              />
            </div>
            <WeekRangeFilter
              from={dateRange?.startDate || ''}
              to={dateRange?.endDate || ''}
              onChange={(f, t2) => onDateRangeChange?.({ ...dateRange, startDate: f, endDate: t2 })}
              testId="budget-dashboard-week-filter"
            />

            <div className="h-6 w-px bg-gray-300" />

            {/* Display currency switcher — presentation-only filter */}
            <DisplayCurrencySwitcher />
            {onResetFilters && <ResetFiltersButton onClick={onResetFilters} testId="budget-dashboard-reset-filters" />}
          </div>
        </CardContent>
      </Card>

      {noData ? (
        <div className="text-center py-8 text-gray-500" data-testid="budget-dashboard-no-data">
          {t('budget.noBudgetDataForPeriod')}
        </div>
      ) : (<>

      {/* Summary Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">{t('budget.totalRevenue')}</p>
                <p className="flex flex-wrap items-center gap-x-1 text-lg font-bold text-green-600"><span className="whitespace-nowrap">{formatCurrency(totals.revenue)}</span><CopyNumberButton value={totals.revenue} size="sm" testId="copy-dash-revenue" /></p>
              </div>
              <TrendingUp className="h-6 w-6 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">{t('budget.totalCosts')}</p>
                <p className="flex flex-wrap items-center gap-x-1 text-lg font-bold text-red-600"><span className="whitespace-nowrap">{formatCurrency(totals.costs)}</span><CopyNumberButton value={totals.costs} size="sm" testId="copy-dash-costs" /></p>
              </div>
              <TrendingDown className="h-6 w-6 text-red-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">{t('budget.totalProfit')}</p>
                <p className={`flex flex-wrap items-center gap-x-1 text-lg font-bold ${totals.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  <span className="whitespace-nowrap">{formatCurrency(totals.profit)}</span><CopyNumberButton value={totals.profit} size="sm" testId="copy-dash-profit" />
                </p>
              </div>
              <DollarSign className="h-6 w-6 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div>
              <p className="text-xs text-gray-500">{t('budget.avgMargin')}</p>
              <p className={`text-lg font-bold ${healthIndicators.avgMargin >= 20 ? 'text-green-600' : healthIndicators.avgMargin >= 0 ? 'text-yellow-600' : 'text-red-600'}`}>
                {healthIndicators.avgMargin.toFixed(1)}%<CopyNumberButton value={healthIndicators.avgMargin} decimals={1} size="sm" className="ml-1" testId="copy-dash-margin" />
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div>
              <p className="text-xs text-gray-500">{t('budget.totalHours')}</p>
              <p className="flex flex-wrap items-center gap-x-1 text-lg font-bold"><span className="whitespace-nowrap">{totals.hours.toLocaleString()}</span><CopyNumberButton value={totals.hours} trim size="sm" testId="copy-dash-hours" /></p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">{t('budget.atRisk')}</p>
                <p className="text-lg font-bold text-orange-600">{healthIndicators.atRiskProjects + healthIndicators.lossProjects}</p>
              </div>
              {(healthIndicators.atRiskProjects + healthIndicators.lossProjects) > 0 && (
                <AlertTriangle className="h-6 w-6 text-orange-500" />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Budget vs Actual Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('budget.revenueVsCostsByProject')}</CardTitle>
            <CardDescription>{t('budget.topProjectsComparison')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80 min-h-[320px]">
              {chartsReady && (
              <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={100} debounce={50}>
                <BarChart data={budgetVsActualData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45} 
                    textAnchor="end" 
                    height={80}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis 
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="revenue" name={t('budget.revenue')} fill="#22c55e" />
                  <Bar dataKey="costs" name={t('budget.costs')} fill="#ef4444" />
                </BarChart>
              </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Expense by Category Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('budget.costBreakdownByCategory')}</CardTitle>
            <CardDescription>{t('budget.includingLaborAndExpenses')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64 min-h-[240px]">
              {expenseByCategoryData.length > 0 ? (
                chartsReady && <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={100} debounce={50}>
                  <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                    <Pie
                      data={expenseByCategoryData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderPieLabel}
                      outerRadius="85%"
                      paddingAngle={1}
                      minAngle={2}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {expenseByCategoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500">
                  {t('budget.noExpenseDataAvailable')}
                </div>
              )}
            </div>
            <PieLegend data={expenseByCategoryData} format={formatCurrency} />
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profit Margin by Project */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('budget.profitMarginByProject')}</CardTitle>
            <CardDescription>{t('budget.marginLegendNote')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80 min-h-[320px]">
              {profitMarginData.length > 0 ? (
                chartsReady && <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={100} debounce={50}>
                  <BarChart data={profitMarginData} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 'auto']} unit="%" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
                    <Tooltip 
                      formatter={(value) => `${value.toFixed(1)}%`}
                      labelFormatter={(label, payload) => payload[0]?.payload?.fullName || label}
                    />
                    <Bar 
                      dataKey="margin" 
                      name={t('budget.marginPct')}
                    >
                      {profitMarginData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.isHealthy ? '#22c55e' : '#ef4444'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500">
                  {t('budget.noProjectsWithRevenueData')}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Expense Trend Over Time */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('budget.expenseTrend')}</CardTitle>
            <CardDescription>{t('budget.approvedExpensesOverLast6Months')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80 min-h-[320px]">
              {expenseTrendData.length > 0 ? (
                chartsReady && <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={100} debounce={50}>
                  <AreaChart data={expenseTrendData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Area type="monotone" dataKey="travel" name={t('budget.costCategory.travel')} stackId="1" stroke={CATEGORY_COLORS.travel} fill={CATEGORY_COLORS.travel} />
                    <Area type="monotone" dataKey="accommodation" name={t('budget.costCategory.accommodation')} stackId="1" stroke={CATEGORY_COLORS.accommodation} fill={CATEGORY_COLORS.accommodation} />
                    <Area type="monotone" dataKey="tools" name={t('budget.costCategory.tools')} stackId="1" stroke={CATEGORY_COLORS.tools} fill={CATEGORY_COLORS.tools} />
                    <Area type="monotone" dataKey="consumables" name={t('budget.costCategory.consumables')} stackId="1" stroke={CATEGORY_COLORS.consumables} fill={CATEGORY_COLORS.consumables} />
                    <Area type="monotone" dataKey="meals" name={t('budget.costCategory.meals')} stackId="1" stroke={CATEGORY_COLORS.meals} fill={CATEGORY_COLORS.meals} />
                    <Area type="monotone" dataKey="other" name={t('budget.costCategory.other')} stackId="1" stroke={CATEGORY_COLORS.other} fill={CATEGORY_COLORS.other} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500">
                  {t('budget.noExpenseTrendDataAvailable')}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Project Status Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('budget.projectFinancialHealth')}</CardTitle>
          <CardDescription>{t('budget.quickOverviewOfAllProjects')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {projects.map(project => {
              const summary = budgetSummaries[project.id];
              if (!summary) return null;
              
              const margin = summary.profit_margin_percent || 0;
              const status = margin >= 20 ? 'healthy' : margin >= 0 ? 'warning' : 'danger';
              const statusColors = {
                healthy: 'bg-green-100 text-green-800 border-green-300',
                warning: 'bg-yellow-100 text-yellow-800 border-yellow-300',
                danger: 'bg-red-100 text-red-800 border-red-300'
              };
              
              return (
                <Badge 
                  key={project.id} 
                  variant="outline"
                  className={`${statusColors[status]} px-3 py-1`}
                >
                  {project.name}: {margin.toFixed(1)}%
                </Badge>
              );
            })}
          </div>
        </CardContent>
      </Card>
      </>)}
    </div>
  );
};

export default BudgetDashboard;
